import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined'
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined'
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from '@mui/material'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import { useFeatureEnabled } from '@/features/feature-flags/useFeatureFlags'
import useAuth from '@/hooks/useAuth'
import { FEEDBACK_ROLES } from '@/lib/roles'
import { getDashboardPageTitle } from '@/routeConfig'
import { hasRole } from '@/utils/hasRole'
import { useSubmitFeedback } from './api'

const COMMENT_LIMIT = 2000

/**
 * "Feedback on this page" in the top bar, for staff and admins only (never
 * pupils). Text only: the page path, org and roles are recorded server-side,
 * and nothing on screen is captured. Renders nothing while the `page_feedback`
 * feature flag is off (or still loading).
 */
export default function PageFeedbackButton() {
    const { user } = useAuth()
    const { pathname } = useLocation()
    const [open, setOpen] = useState(false)
    const [rating, setRating] = useState(null)
    const [comment, setComment] = useState('')
    const submit = useSubmitFeedback()
    const enabled = useFeatureEnabled('page_feedback')

    if (!enabled || !user || !hasRole(user, FEEDBACK_ROLES)) return null

    const pageTitle = getDashboardPageTitle(pathname) ?? ''
    const canSend = Boolean(rating || comment.trim()) && !submit.isPending

    const close = () => {
        setOpen(false)
        setRating(null)
        setComment('')
    }

    const send = () => {
        submit.mutate(
            {
                path: pathname,
                page_title: pageTitle,
                rating: rating ?? '',
                comment
            },
            {
                onSuccess: () => {
                    toast.success('Thanks, your feedback has been sent')
                    close()
                },
                onError: () => toast.error('Could not send your feedback')
            }
        )
    }

    return (
        <>
            <Tooltip title="Feedback on this page">
                <IconButton
                    aria-label="Feedback on this page"
                    onClick={() => setOpen(true)}
                >
                    <FeedbackOutlinedIcon />
                </IconButton>
            </Tooltip>
            <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
                <DialogTitle>
                    Feedback on {pageTitle ? `“${pageTitle}”` : 'this page'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Is this page useful?
                        </Typography>
                        <ToggleButtonGroup
                            exclusive
                            value={rating}
                            onChange={(_, value) => setRating(value)}
                            aria-label="Rating"
                        >
                            <ToggleButton value="up" aria-label="Helpful">
                                <ThumbUpOutlinedIcon />
                            </ToggleButton>
                            <ToggleButton value="down" aria-label="Not helpful">
                                <ThumbDownOutlinedIcon />
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <TextField
                            label="What would make it better? (optional)"
                            multiline
                            minRows={3}
                            value={comment}
                            onChange={(e) =>
                                setComment(
                                    e.target.value.slice(0, COMMENT_LIMIT)
                                )
                            }
                            helperText="Please don't include anyone's personal details."
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        component={RouterLink}
                        to="/feedback"
                        onClick={close}
                        sx={{ mr: 'auto' }}
                    >
                        My feedback
                    </Button>
                    <Button onClick={close}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={!canSend}
                        onClick={send}
                    >
                        Send
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
